/**
 * Admin Retailers API client — talks to /api/admin-retailers.php.
 */

import { TotalFormat } from '../types';
import type { CaptureMode } from '../config/capturePolicy';

const API_BASE = window.location.hostname === 'localhost' ? 'http://chriscme/api' : '/api';

export type CurrencyCode = 'USD' | 'CAD';
export type { TotalFormat };

/** Trusscore's own square mark — the brand icon for retailer #1 (and for estimates with no retailer) unless one is uploaded. */
export const DEFAULT_BRAND_ICON_URL = '/images/trusscore-square.svg';

/** The icon to show for a retailer in admin lists: its upload, else Trusscore's mark for #1 / none. */
export function brandIconFor(retailerId: number | null, uploaded: string | null | undefined): string | null {
  if (uploaded) return uploaded;
  return retailerId === null || retailerId === 1 ? DEFAULT_BRAND_ICON_URL : null;
}

/**
 * Partner branding theme. All keys optional — runtime falls back to the
 * Trusscore defaults via CSS custom property fallbacks for any missing key.
 * Values are hex colors (#fff, #ffffff, or #ffffffff).
 */
export interface RetailerTheme {
  // --- Nav ---
  navBg?: string;
  navFg?: string;
  navHoverBg?: string;
  // --- Brand ---
  primary?: string;
  accent?: string;
  // --- Primary buttons ---
  buttonBg?: string;
  buttonFg?: string;
  // --- Secondary buttons (slate-style: Back to Edit / Start Over) ---
  secondaryBg?: string;
  secondaryFg?: string;
  // --- Modal title bars (e.g. Detailed Material List Request) ---
  modalHeaderBg?: string;
  modalHeaderFg?: string;
  // --- Bill of Materials table head + total row ---
  tableHeaderBg?: string;
  tableHeaderFg?: string;
  tableTotalBg?: string;
  tableTotalFg?: string;
  // --- Cost total card ---
  // estimateBg = the dark card background; estimateValueColor + AltColor
  // are the two colors the cost figure animates between.
  estimateBg?: string;
  estimateValueColor?: string;
  estimateValueAltColor?: string;
  // --- Footer ---
  footerBg?: string;
  footerFg?: string;
  /** Muted secondary text in the footer (disclaimer + build version line). */
  footerMutedFg?: string;
}

export interface RetailerSplashConfig {
  imageUrl?: string;
  headline?: string;
  body?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  backgroundColor?: string;
}

export interface RetailerNavLink {
  label: string;
  url: string;
  target: '_self' | '_blank';
  showOnEstimator: boolean;
  showOnDesign: boolean;
}

/** 'own' = this retailer's own price book; 'parent' = uses the parent retailer's book. */
export type PricingSource = 'own' | 'parent';

/** A Salesforce Account that IS this retailer (for matching staff by email). */
export interface RetailerSfAccount {
  /** 18-character Salesforce Account id (the server normalizes 15-char ids). */
  sfAccountId: string;
  /** Optional label, e.g. "Home Hardware (Group)". */
  sfAccountName: string | null;
  /** Also match Contacts whose Account has this id as its Parent Account. */
  includeChildren: boolean;
}

/** Which retailer's price book actually prices this retailer. */
export interface RetailerPricingInfo {
  priceBookRetailerId: number;
  priceBookRetailerName: string | null;
  inherited: boolean;
}

export interface AdminRetailer {
  id: number;
  name: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  stateProvince: string | null;
  postalCode: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  latitude: number | null;
  longitude: number | null;
  isNational: boolean;
  isActive: boolean;
  // --- Hierarchy + staff-account matching ---
  /** The chain / group this location belongs to; null = top-level retailer. */
  parentRetailerId: number | null;
  pricingSource: PricingSource;
  /** Staff email domains that propose a registrant for this retailer (lowercase, no @). */
  emailDomains: string[];
  /** Salesforce Accounts that are this retailer. */
  sfAccounts: RetailerSfAccount[];
  /** Server-computed: the retailer whose book prices this one (= id unless inheriting). */
  priceBookRetailerId?: number;
  priceBookRetailerName?: string | null;
  pricingInherited?: boolean;
  parentRetailerName?: string | null;
  currenciesSupported: CurrencyCode[];
  /** Force price/total columns on for all of this retailer's shared estimates + PDFs. */
  showTotals: boolean;
  /** Estimated-total display format: rounded up to $100 (default) or exact 2-decimal. */
  totalFormat: TotalFormat;
  // --- Language ---
  /** Locale this portal opens in before the visitor chooses; null = global default. */
  defaultLocale: string | null;
  /** Locale codes the switcher offers on this portal; null = every active locale. */
  localesSupported: string[] | null;
  // --- Guest capture + lead routing ---
  /**
   * 'full' — today's ten-field guest form, materials list gated behind it.
   * 'minimal' — estimate name + email only, and the gate comes off entirely.
   */
  captureMode: CaptureMode;
  /** This retailer's own Pardot form handler; blank falls back to the global one. */
  pardotFormHandlerUrl: string | null;
  /** On-behalf saves by this retailer's staff post here (null = the global on-behalf handler). */
  pardotOnBehalfHandlerUrl: string | null;
  /** false hides the sign-in / create-account UI on this retailer's portal. */
  accountsEnabled: boolean;
  // --- Partner branding ---
  subdomain: string | null;
  isPartner: boolean;
  theme: RetailerTheme | null;
  logoUrl: string | null;
  logoAlt: string | null;
  /** Square 64–512 px icon shown beside this retailer's estimates in admin lists. */
  brandIconUrl: string | null;
  splashConfig: RetailerSplashConfig | null;
  navLinks: RetailerNavLink[] | null;
  underConstructionMessage: string | null;
  /**
   * Location-map defaults for this portal: Salesforce account ids / buying group /
   * regions merged into any map whose filter has useRetailerFilter on.
   */
  mapFilter: RetailerMapFilter | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  productCount?: number;
  activeProductCount?: number;
  /** Relationship counts (list view): child retailers, linked share-map stores, approved / waiting staff. */
  childCount?: number;
  storeCount?: number;
  memberCount?: number;
  pendingMemberCount?: number;
  eligibility?: RetailerEligibility;
}

export interface RetailerEligibility {
  eligible: boolean;
  missingAcronyms: string[];
}

export interface RetailerProductRow {
  productId: number;
  productSlug: string;
  sku: string;
  name: string;
  acronym: string;
  category: string;
  color: string;
  lengthFeet: number;
  productActive: boolean;
  carried: boolean;
  priceUsd: number | null;
  priceCad: number | null;
  isActive: boolean | null;
  needsReview: boolean | null;
  msrpUsd: number | null;
  msrpCad: number | null;
  /** Stored markup % over MSRP. null = no markup (price is manual). */
  percentMarkupUsd: number | null;
  percentMarkupCad: number | null;
  /** Per-retailer custom field values for this product (when defs apply). */
  customFields?: RetailerProductCustomField[];
}

/** A per-retailer custom field value attached to a carried product. */
export interface RetailerProductCustomField {
  fieldDefId: number;
  fieldKey: string;
  label: string;
  dataType: 'numeric' | 'varchar' | 'url';
  value: string | null;
}

/** Field-definition shape echoed in retailer detail (for the editing UI). */
export interface RetailerFieldDef {
  id: number;
  fieldKey: string;
  label: string;
  dataType: 'numeric' | 'varchar' | 'url';
  urlTemplate: string | null;
  isActive: boolean;
}

export interface RetailerListResponse {
  retailers: AdminRetailer[];
  requiredAcronyms: string[];
}

export interface RetailerDetailResponse {
  retailer: AdminRetailer;
  /** When `pricing.inherited`, these are the PARENT's rows (read-only in the UI). */
  products: RetailerProductRow[];
  pricing?: RetailerPricingInfo;
  /** Custom field definitions that apply to this retailer (for value editing). */
  fieldDefs?: RetailerFieldDef[];
  requiredAcronyms: string[];
  eligibility: RetailerEligibility;
  usage: { userCount: number; shareCount: number };
}

export interface RetailerFieldsPatch {
  name?: string;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  stateProvince?: string | null;
  postalCode?: string | null;
  country?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  isNational?: boolean;
  isActive?: boolean;
  parentRetailerId?: number | null;
  pricingSource?: PricingSource;
  emailDomains?: string[];
  sfAccounts?: RetailerSfAccount[];
  currenciesSupported?: CurrencyCode[];
  showTotals?: boolean;
  totalFormat?: TotalFormat;
  defaultLocale?: string | null;
  localesSupported?: string[] | null;
  captureMode?: CaptureMode;
  pardotFormHandlerUrl?: string | null;
  pardotOnBehalfHandlerUrl?: string | null;
  accountsEnabled?: boolean;
  subdomain?: string | null;
  isPartner?: boolean;
  theme?: RetailerTheme | null;
  logoUrl?: string | null;
  logoAlt?: string | null;
  brandIconUrl?: string | null;
  splashConfig?: RetailerSplashConfig | null;
  navLinks?: RetailerNavLink[] | null;
  underConstructionMessage?: string | null;
  mapFilter?: RetailerMapFilter | null;
}

export interface RetailerMapFilter {
  ids?: string[];
  buyingGroups?: string[];
  regions?: string[];
  countries?: string[];
}

export interface RetailerCreatePayload {
  retailer: RetailerFieldsPatch & { name: string };
  products: { mode: 'all' | 'subset' | 'none'; productIds?: number[] };
}

export interface RetailerProductUpdate {
  productId: number;
  priceUsd?: number;
  priceCad?: number;
  isActive?: boolean;
  needsReview?: boolean;
  /**
   * Per-currency percent markup over MSRP. Setting a number recomputes
   * the corresponding price column to MSRP × (1 + N/100). Setting null
   * clears the markup; the price column is left as-is.
   */
  percentMarkupUsd?: number | null;
  percentMarkupCad?: number | null;
  /** Per-retailer custom field values to upsert (empty value deletes). */
  customFields?: Array<{ fieldDefId: number; value: string | null }>;
}

export interface RetailerPatchPayload {
  retailer?: RetailerFieldsPatch;
  products?: {
    add?: number[];
    remove?: number[];
    update?: RetailerProductUpdate[];
  };
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export async function fetchAdminRetailers(): Promise<RetailerListResponse> {
  const res = await fetch(`${API_BASE}/admin-retailers.php`, { credentials: 'include' });
  return handle<RetailerListResponse>(res);
}

export async function fetchAdminRetailer(id: number): Promise<RetailerDetailResponse> {
  const res = await fetch(`${API_BASE}/admin-retailers.php?id=${id}`, { credentials: 'include' });
  return handle<RetailerDetailResponse>(res);
}

export async function createAdminRetailer(payload: RetailerCreatePayload): Promise<{ retailer: AdminRetailer; attached: number; eligibility: RetailerEligibility }> {
  const res = await fetch(`${API_BASE}/admin-retailers.php`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handle(res);
}

export async function patchAdminRetailer(id: number, payload: RetailerPatchPayload): Promise<{ retailer: AdminRetailer; eligibility: RetailerEligibility }> {
  const res = await fetch(`${API_BASE}/admin-retailers.php?id=${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handle(res);
}

export async function deleteAdminRetailer(id: number, confirmName: string): Promise<void> {
  const params = new URLSearchParams({ id: String(id), confirmName });
  const res = await fetch(`${API_BASE}/admin-retailers.php?${params}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Delete failed (${res.status})`);
  }
}

// ---------------------------------------------------------------------------
// Relationships (read-only picture of one retailer: parent, children, stores,
// Salesforce accounts, members and what each member sees)
// ---------------------------------------------------------------------------

/** retailer = no location assigned but the retailer has none to assign: sees the whole retailer. */
export type MemberScope = 'all' | 'locations' | 'none';

export interface RetailerRelationships {
  retailer: { id: number; name: string };
  parent: { id: number; name: string } | null;
  children: { id: number; name: string; pricingInherited: boolean; isActive: boolean }[];
  sfAccounts: { sfAccountId: string; sfAccountName: string | null; includeChildren: boolean; storeId: number | null; storeName: string | null }[];
  stores: {
    id: number; name: string; kind: 'retailer' | 'installer'; city: string | null; region: string | null; sfid: string;
    linkMode: 'auto' | 'manual' | 'none'; retailerId: number; retailerName: string; memberCount: number;
  }[];
  members: {
    id: number; firstName: string | null; lastName: string | null; email: string | null;
    status: 'pending' | 'approved' | 'rejected' | 'unmatched'; role: 'viewer' | 'manager'; roleSource: string; source: string;
    sfAccountId: string | null; sfAccountName: string | null; allLocations: boolean; locations: { id: number; name: string }[]; scope: MemberScope;
  }[];
}

export async function fetchRetailerRelationships(id: number): Promise<RetailerRelationships> {
  const res = await fetch(`${API_BASE}/admin-retailer-relationships.php?id=${id}`, { credentials: 'include' });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  return res.json() as Promise<RetailerRelationships>;
}
